import React, { useEffect } from "react";
import { WagmiProvider, useAccount } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { 
  RainbowKitProvider, 
  getDefaultConfig, 
  darkTheme,
} from "@rainbow-me/rainbowkit";
import { bsc } from "wagmi/chains";
import "@rainbow-me/rainbowkit/styles.css";
import { saveUserWallet } from "../services/userService";

// Configure chains - ONLY BNB Chain (BSC)
const chains = [bsc];

// Get project ID from WalletConnect Cloud
// You can get a free project ID at https://cloud.walletconnect.com
const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "demo-project-id";

// Warn if using demo project ID
if (projectId === "demo-project-id") {
  console.warn(
    "⚠️ WalletConnect: Using demo project ID. Please set REACT_APP_WALLETCONNECT_PROJECT_ID in your .env file.\n" +
    "Get your free project ID at: https://cloud.walletconnect.com"
  );
}

// Create wagmi config with RainbowKit
// See: https://rainbowkit.com/docs/installation#configure
export const wagmiConfig = getDefaultConfig({
  appName: "IO Trader",
  projectId,
  chains,
  ssr: false, // Set to true if using SSR
  
  // Optional: Customize wallet connectors
  // wallets: [
  //   {
  //     groupName: 'Recommended',
  //     wallets: [metaMaskWallet, walletConnectWallet],
  //   },
  // ],
});

// Keep config for backward compatibility
const config = wagmiConfig;

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Component to save user wallet when connected
const WalletSync = () => {
  const { address, isConnected } = useAccount();

  useEffect(() => {
    const saveWallet = async () => {
      if (isConnected && address) {
        try {
          await saveUserWallet(address);
          console.log("User wallet saved to Firebase");
        } catch (error) {
          console.error("Error saving user wallet:", error);
        }
      }
    };

    saveWallet();
  }, [address, isConnected]);

  return null;
};

const WalletProvider = ({ children }) => {
  // Custom dark theme matching your app's design
  // See: https://rainbowkit.com/docs/theming
  const customTheme = darkTheme({
    accentColor: "#eab308", // Yellow-500 - matches your brand
    accentColorForeground: "#000000", // Black text on yellow
    borderRadius: "medium",
    fontStack: "system",
    overlayBlur: "small",
  });

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          theme={customTheme}
          // Initial chain: BNB Chain (BSC) only
          initialChain={bsc}
        >
          <WalletSync />
          {children}
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default WalletProvider;
